import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardContent, Badge, Skeleton, ErrorState, Button, EmptyState } from '@/components/ui'
import { Shield, Info } from 'lucide-react'
import { api, type AppScopeInfo } from '@/api/client'

export function AppScopesPage() {
  const { orgSlug, appId } = useParams()
  const [scopes, setScopes] = useState<AppScopeInfo[]>([])
  const [everyoneScopes, setEveryoneScopes] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      if (!orgSlug || !appId) return
      try {
        setError(null)
        const [scopesRes, everyoneRes] = await Promise.all([
          api.apps.listScopes(orgSlug, appId),
          api.apps.getEveryone(orgSlug, appId),
        ])
        setScopes(scopesRes.scopes)
        setEveryoneScopes(everyoneRes.scopes)
      } catch (err) {
        console.error('Failed to fetch scopes:', err)
        setError(err instanceof Error ? err.message : 'Failed to load scopes')
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [orgSlug, appId])

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-5 w-32" />
          </CardHeader>
          <CardContent className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center justify-between py-3 border-b border-[var(--border-subtle)] last:border-0">
                <div className="space-y-1">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
                <Skeleton className="h-6 w-16" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <ErrorState
        title="Failed to load scopes"
        description={error}
        action={<Button onClick={() => window.location.reload()}>Try Again</Button>}
      />
    )
  }

  if (scopes.length === 0) {
    return (
      <EmptyState
        icon={Shield}
        title="No scopes defined"
        description="Scopes are defined in your app's code using the SDK. Deploy your app to sync scopes."
      />
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
        <Info className="h-4 w-4" />
        <span>Scopes are defined in your app's code and synced automatically on deploy.</span>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Application Scopes</CardTitle>
        </CardHeader>
        <CardContent className="divide-y divide-[var(--border-subtle)]">
          {scopes.map((scope) => (
            <div key={scope.id} className="flex items-center justify-between py-4 first:pt-0 last:pb-0">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <code className="text-sm font-medium text-[var(--text-primary)]">{scope.name}</code>
                  {everyoneScopes.includes(scope.name) && (
                    <Badge variant="default">@everyone</Badge>
                  )}
                  {scope.status === 'deprecated' && (
                    <Badge variant="warning">Deprecated</Badge>
                  )}
                </div>
                <p className="text-sm text-[var(--text-muted)]">
                  {scope.description || 'No description'}
                </p>
              </div>
              <Badge variant={scope.status === 'active' ? 'success' : 'default'}>
                {scope.status}
              </Badge>
            </div>
          ))}
        </CardContent>
      </Card>

      {everyoneScopes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>@everyone Role</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-[var(--text-secondary)] mb-4">
              These scopes are automatically granted to all users who authorize your app.
            </p>
            <div className="flex flex-wrap gap-2">
              {everyoneScopes.map((scope) => (
                <Badge key={scope} variant="default">{scope}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
